import { createFileRoute } from "@tanstack/react-router";
import { Panel } from "@/ui/Panel";
import { ErrorState, Skeleton } from "@/ui/States";
import { Runway } from "@/features/billing/Runway";
import { Allowance } from "@/features/plan/Allowance";
import { UsagePanel } from "@/features/billing/panels";
import { PurchaseNotice, PurchasesPanel } from "@/features/billing/Purchases";
import { awaitingConfirmation, useCredit, useRental } from "@/features/billing/rental";

export const Route = createFileRoute("/_app/o/$orgId/billing/")({
  component: TheRental,
  /* `?bought=<id>` is where the card processor sends somebody back to.
     Validated rather than read loosely, and kept in the address rather than
     in a shared state, so the receipt survives a reload — a customer whose
     browser restored the tab should still see what happened. */
  validateSearch: (search: Record<string, unknown>): { bought?: string } =>
    typeof search.bought === "string" && search.bought.length === 26
      ? { bought: search.bought }
      : {},
});

/**
 * The rental: how long is left, what it is costing, and what has been used.
 *
 * # THE ORDER THESE ARE IN
 *
 * Time, then ceilings, then what ran — which is the order of the questions.
 * "How long have I got" is asked on every visit, "how close am I to a limit"
 * when something has just been refused, and "what have I been using" when
 * somebody is deciding whether to move up. An older version of this screen
 * had that backwards: seven usage bars above the figure somebody came for.
 *
 * # THE RECEIPT IS HERE, AND SO ARE THE DOCUMENTS
 *
 * The card processor returns a customer to this tab, so this is where it has
 * to say what happened. It used to say nothing: the balance moves on the
 * processor's confirmation rather than on the redirect, so the figure above
 * was still the old one, and the purchase itself was listed two tabs away
 * under a heading about documents. The state of the payment is now a notice
 * here, and the invoices are a table under it.
 *
 * # THE PLAN CHOOSER IS NOT HERE ANY MORE
 *
 * It is on the ordering screen, with the other two things that spend money.
 * This screen states what is rented; the one next door is where that is
 * changed. Reading a statement and buying capacity turned out to be different
 * errands, and the chooser sitting between the runway and the ceilings made
 * this page a form as much as a record.
 */
function TheRental() {
  const { orgId } = Route.useParams();
  const { bought } = Route.useSearch();
  const credit = useCredit(orgId);
  // The balance follows the purchase. Both are polled for the few seconds
  // between the redirect and the processor's confirmation, so the receipt
  // resolves itself and the figure above it catches up without a reload.
  const pending = awaitingConfirmation(credit.data);
  const rental = useRental(orgId, { refetchInterval: pending ? 3_000 : false });
  const purchases = credit.data?.purchases ?? [];

  if (rental.isLoading) return <Skeleton className="h-64 w-full rounded-xl" />;
  if (rental.isError) {
    return <ErrorState error={rental.error} onRetry={() => void rental.refetch()} />;
  }
  if (!rental.data) return null;

  return (
    <div className="flex flex-col gap-6">
      <PurchaseNotice purchases={purchases} bought={bought} />

      <Panel>
        <Runway rental={rental.data} />
      </Panel>

      <PurchasesPanel orgId={orgId} purchases={purchases} loading={credit.isLoading} />

      <Allowance orgId={orgId} />

      {/* What actually ran, as information.
          The meter survived the change and the figures are worth seeing — a
          customer wondering whether to move up a plan wants to know what they
          have been using. What it no longer carries is money: the readings
          are not charged, so a cost column beside them would be a price the
          platform does not take. */}
      <UsagePanel base={`/organisations/${orgId}/billing`} orgId={orgId} />
    </div>
  );
}
